import { huntCategoryMap } from '../ccm'
import { EnvironmentTool, FieldMapping, SupportedTools } from '../interfaces'
import type { GroupedByHuntCategory, GroupedQuery } from '../rtd.types'
import { toolFactory } from '../tools'
import { CrowdStrikeLogScaleTool } from '../tools/crowdstrike-logscale.tool'
import { GoogleSecOpsTool } from '../tools/google-secops'
import { MicrosoftDefenderTool } from '../tools/microsoft-defender.tool'
import { MicrosoftSentinelTool } from '../tools/microsoft-sentinel.tool'
import { PaloAltoCortexXDRTool } from '../tools/paloalto-cortex-xdr.tool'
import type { AstGroup, AstNode } from '../tools/render/types'
import { SentinelOnePowerQueryTool } from '../tools/sentinelone-powerquery.tool'
import { SentinelOneSingularityTool } from '../tools/sentinelone-singularity.tool'
import { SentinelOneTool } from '../tools/sentinelone.tool'
import { SplunkTool } from '../tools/splunk.tool'
import { TrendMicroVisionOneTool } from '../tools/trendmicro-visionone.tool'

import { buildIocAst } from './ioc.ast-kit'
import {
  buildIocAstsByCategory,
  pruneAstByExcludedFields,
  pruneAstByFields,
} from './ioc.category-kit'
import { IocItem } from './ioc.types'

export interface RtdResult {
  query: string
  category: string
}

export function renderIocForTool(
  iocs: IocItem[],
  toolName: SupportedTools,
  mode: 'aggregated' | 'per-category' = 'per-category',
  envTool?: EnvironmentTool,
): RtdResult[] {
  const tool = toolFactory(toolName)
  if (!tool.supportsRtd()) {
    throw new Error(`RTD rendering not supported for tool: ${toolName}`)
  }
  // Safe — supportsRtd() guarantees renderTuning exists
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const render = getToolRenderer(tool, toolName)!

  if (mode === 'aggregated') {
    const ast = buildIocAst(iocs)
    const baseQuery = render(ast)
    const cats = ast.directives?.datasource?.category
    const category = Array.isArray(cats) ? cats.join('+') : (cats ?? 'ioc')

    const query = applyFieldMapping(tool, baseQuery, category, envTool)
    return [{ query, category }]
  }

  // per-category mode
  const astMap = buildIocAstsByCategory(iocs)
  const results: RtdResult[] = []
  for (const [category, ast] of astMap) {
    const baseQuery = render(ast)
    const query = applyFieldMapping(tool, baseQuery, category, envTool)
    results.push({ query, category })
  }
  return results
}

/**
 * Render IOC queries grouped by hunt category with per-log-category
 * field mapping resolution.
 *
 * For each hunt category, looks up the associated log categories from
 * CCM_HUNT_CATEGORY_MAP, finds matching field mappings from the user's
 * environment, and renders the query with those mappings applied.
 */
export function renderIocForToolGrouped(
  iocs: IocItem[],
  toolName: SupportedTools,
  envTool?: EnvironmentTool,
  huntCategories?: string[],
): GroupedByHuntCategory {
  const tool = toolFactory(toolName)
  if (!tool.supportsRtd()) {
    throw new Error(`RTD rendering not supported for tool: ${toolName}`)
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const render = getToolRenderer(tool, toolName)!
  const astMap = buildIocAstsByCategory(iocs)

  // SIEM-style aggregators (incl. NDR like Corelight Investigator) take the matrix path;
  // single-telemetry tools (EDR/XDR) take the single-source path. 'misc' tools are gated
  // out earlier by `supportsRtd()` and never reach here.
  if (tool.toolKind === 'siem' || tool.toolKind === 'ndr') {
    return renderSiemGrouped(tool, render, astMap, envTool, huntCategories)
  }
  return renderSingleSourceGrouped(
    tool,
    render,
    astMap,
    envTool,
    huntCategories,
  )
}

/**
 * SIEM path: iterate each customer-mapped log_category and emit one query per
 * (huntCat, logCat) pair, pruning predicates that the matrix says the log source
 * doesn't carry for that hunt_category.
 */
function renderSiemGrouped(
  tool: ReturnType<typeof toolFactory>,
  render: (ast: AstNode) => string,
  astMap: Map<string, AstGroup>,
  envTool: EnvironmentTool | undefined,
  huntCategories: string[] | undefined,
): GroupedByHuntCategory {
  const customMappingSupported = tool.customMapping
  const mappingsByCategory: Record<string, FieldMapping[]> = {}
  if (envTool?.fieldMappings) {
    for (const fm of envTool.fieldMappings) {
      if (!mappingsByCategory[fm.category]) {
        mappingsByCategory[fm.category] = []
      }
      mappingsByCategory[fm.category].push(fm)
    }
  }

  const categoriesToProcess = huntCategories?.length
    ? huntCategories.filter((hc) => hc in huntCategoryMap)
    : Object.keys(huntCategoryMap)

  const toolLogCategories = tool.supportedLogCategories?.()
  const toolLogCatSet = toolLogCategories
    ? new Set(toolLogCategories)
    : undefined

  const result: GroupedByHuntCategory = {}
  for (const huntCategory of categoriesToProcess) {
    const mapEntry = huntCategoryMap[huntCategory]
    const allLogCategories = Object.keys(mapEntry.ccm_log_categories)
    const logCategories = toolLogCatSet
      ? allLogCategories.filter((lc) => toolLogCatSet.has(lc))
      : allLogCategories

    const ast = astMap.get(huntCategory)
    if (!ast) {
      result[huntCategory] = { name: mapEntry.name, queries: [] }
      continue
    }

    const queries: GroupedQuery[] = []
    // Tool-level blacklist for no-prelude SIEM tools (e.g. Elastic). Without
    // a prelude to scope events, generic fields like process_path leak into
    // non-core categories and duplicate process_creation. Populated per
    // category in the tool's datasourceMap.any.<huntCategory>.unsupportedFields.
    // SIEM tools with preludes leave this empty — the prelude is the scoping
    // mechanism and process_path-under-registry_event is legitimately useful.
    const toolExcluded = new Set(
      tool.datasourceMap?.[tool.toolName]?.['any']?.[huntCategory]
        ?.unsupportedFields ?? [],
    )

    for (const logCategory of logCategories) {
      const allowed = new Set(mapEntry.ccm_log_categories[logCategory])
      const matrixPruned = pruneAstByFields(ast, allowed)
      if (!matrixPruned) continue // no fields from this log source carry this hunt category
      const prunedAst = toolExcluded.size
        ? pruneAstByExcludedFields(matrixPruned, toolExcluded)
        : matrixPruned
      if (!prunedAst) continue // tool-level blacklist dropped every predicate

      const scopedAst = attachLogCategory(prunedAst, logCategory)
      const baseQuery = render(scopedAst)
      const templateFields = extractTemplateFields(baseQuery)
      const mappings = mappingsByCategory[logCategory]

      if (customMappingSupported && mappings?.length) {
        for (const fm of mappings) {
          const query = tool.renderContentTemplate(baseQuery, undefined, fm, {
            includeToolPrefix: false,
          })
          queries.push({
            query,
            logCategory,
            name: fm.name,
            matched: true,
            missingFields: findMissingFields(templateFields, fm),
          })
        }
      } else {
        const query = tool.renderContentTemplate(
          baseQuery,
          undefined,
          undefined,
          { includeToolPrefix: false },
        )
        queries.push({
          query,
          logCategory,
          name: undefined,
          matched: !customMappingSupported,
          missingFields: [],
        })
      }
    }

    result[huntCategory] = { name: mapEntry.name, queries }
  }

  return result
}

/**
 * EDR / XDR path: single-telemetry-source tools. Emit one query per hunt_category
 * the tool's datasourceMap declares. No matrix pruning — field availability is
 * governed by the tool's own CCM_TOOL_RENDER_FIELDS mapping. Output is tagged
 * with a fixed log_category label derived from toolKind ('edr' or 'xdr').
 */
function renderSingleSourceGrouped(
  tool: ReturnType<typeof toolFactory>,
  render: (ast: AstNode) => string,
  astMap: Map<string, AstGroup>,
  envTool: EnvironmentTool | undefined,
  huntCategories: string[] | undefined,
): GroupedByHuntCategory {
  const logCategory = tool.toolKind // 'edr' or 'xdr'
  const mapping = envTool?.fieldMappings?.find(
    (fm) => fm.category === logCategory,
  )
  const anyMap = tool.datasourceMap?.[tool.toolName]?.['any'] ?? {}
  const supportedHuntCats = new Set(Object.keys(anyMap))

  const categoriesToProcess = huntCategories?.length
    ? huntCategories.filter((hc) => hc in huntCategoryMap)
    : Object.keys(huntCategoryMap)

  const result: GroupedByHuntCategory = {}
  for (const huntCategory of categoriesToProcess) {
    const mapEntry = huntCategoryMap[huntCategory]
    const rawAst = astMap.get(huntCategory)
    if (!rawAst || !supportedHuntCats.has(huntCategory)) {
      result[huntCategory] = { name: mapEntry.name, queries: [] }
      continue
    }

    // Per-tool per-category field pruning — drop predicates whose CCM field
    // isn't carried by this category's event types for this tool.
    const excluded = new Set(anyMap[huntCategory]?.unsupportedFields ?? [])
    const ast = pruneAstByExcludedFields(rawAst, excluded)
    if (!ast) {
      result[huntCategory] = { name: mapEntry.name, queries: [] }
      continue
    }

    const baseQuery = render(ast)
    const templateFields = extractTemplateFields(baseQuery)
    const customMappingSupported = tool.customMapping
    const query = tool.renderContentTemplate(baseQuery, undefined, mapping, {
      includeToolPrefix: false,
    })

    result[huntCategory] = {
      name: mapEntry.name,
      queries: [
        {
          query,
          logCategory,
          name: mapping?.name,
          matched: !customMappingSupported || Boolean(mapping),
          missingFields:
            customMappingSupported && mapping
              ? findMissingFields(templateFields, mapping)
              : [],
        },
      ],
    }
  }
  return result
}

/**
 * Clone the root AST group with `logCategory` attached to its datasource
 * directives so tool-specific prelude builders can scope event codes
 * (and any future log-source-aware logic) to the customer's log source.
 */
function attachLogCategory(ast: AstNode, logCategory: string): AstNode {
  if (ast.kind !== 'group') return ast
  const group = ast as AstGroup
  const directives = group.directives ?? {}
  const datasource = { ...(directives.datasource ?? {}), logCategory }
  return { ...group, directives: { ...directives, datasource } }
}

/**
 * Extract all CCM field references from an Eta template string.
 * Matches simple interpolation tags like {{!=fieldName!}} and ignores
 * complex expressions (function calls, property access like pivot.x).
 */
function extractTemplateFields(template: string): string[] {
  const fields = new Set<string>()
  const re = /\{\{!=\s*(\w+)\s*!\}\}/g
  let match
  while ((match = re.exec(template)) !== null) {
    fields.add(match[1])
  }
  return [...fields]
}

/**
 * Find which AST fields are missing from a field mapping.
 */
function findMissingFields(astFields: string[], fm: FieldMapping): string[] {
  const defined = new Set<string>()
  if (fm.fields) {
    for (const entry of fm.fields) {
      for (const [key, value] of Object.entries(entry)) {
        if (value) defined.add(key)
      }
    }
  }
  return astFields.filter((f) => !defined.has(f))
}

/**
 * Apply customer field mapping to a rendered query (second-pass Eta rendering).
 * If envTool has a field mapping matching the category, uses it.
 * Otherwise returns the query rendered with tool defaults.
 */
function applyFieldMapping(
  tool: ReturnType<typeof toolFactory>,
  baseQuery: string,
  category: string,
  envTool?: EnvironmentTool,
): string {
  if (!envTool?.fieldMappings?.length) {
    return tool.renderContentTemplate(baseQuery, undefined, undefined, {
      includeToolPrefix: false,
    })
  }

  const mapping = envTool.fieldMappings.find((fm) => fm.category === category)
  return tool.renderContentTemplate(baseQuery, undefined, mapping, {
    includeToolPrefix: false,
  })
}

function getToolRenderer(
  tool: ReturnType<typeof toolFactory>,
  _toolName: SupportedTools,
): ((ast: AstNode) => string) | null {
  if (tool instanceof CrowdStrikeLogScaleTool) {
    return tool.renderCrowdstrikeLogScale
  }
  if (tool instanceof SplunkTool) {
    return tool.renderSplunk
  }
  // Microsoft Defender: KQL table name prelude + dataDisplay + tuning
  if (tool instanceof MicrosoftDefenderTool) {
    return tool.renderMicrosoftDefender
  }
  // Microsoft Sentinel: KQL table name prelude (union for multi-cloud) + dataDisplay + tuning
  if (tool instanceof MicrosoftSentinelTool) {
    return tool.renderMicrosoftSentinel
  }
  // Palo Alto Cortex XDR: XQL dataset + event_type filter prelude + dataDisplay + tuning
  if (tool instanceof PaloAltoCortexXDRTool) {
    return tool.renderPaloAltoCortexXDR
  }
  // SentinelOne Legacy: ObjectType/EventType prelude joined directly (finalize prepends AND)
  if (tool instanceof SentinelOneTool) {
    return tool.renderSentinelOne
  }
  // SentinelOne Singularity: event.category/event.type prelude joined with AND (no finalize)
  if (tool instanceof SentinelOneSingularityTool) {
    return tool.renderSentinelOneSingularity
  }
  // SentinelOne PowerQuery: prelude merged into first | filter line + dataDisplay
  if (tool instanceof SentinelOnePowerQueryTool) {
    return tool.renderSentinelOnePowerQuery
  }
  // Trend Micro Vision One: eventId/eventSubId prelude joined directly (finalize prepends AND)
  if (tool instanceof TrendMicroVisionOneTool) {
    return tool.renderTrendMicroVisionOne
  }
  // Google SecOps: metadata.event_type prelude joined with AND + dataDisplay
  if (tool instanceof GoogleSecOpsTool) {
    return tool.renderGoogleSecOps
  }
  // Fall back to renderTuning if available (base logic, no datasource).
  // CrowdStrike legacy, CB Response, and CB Cloud Investigate land here —
  // their renderTuning emits bare detection predicates with no leading AND
  // glue and no DataDisplay, which is the desired RTD shape. Visibility in
  // renderSingleSourceGrouped is controlled by each tool's datasourceMap
  // (which hunt categories it declares `any` entries for).
  return tool.renderTuning ?? null
}
